import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Send, Sparkles, RefreshCw, MessageSquare, Archive, ExternalLink, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function CitationChip({ c }) {
    const parts = [];
    if (c.doc_number) parts.push(c.doc_number);
    if (c.revision) parts.push(`Rev. ${c.revision}`);
    if (c.step_number) parts.push(`Step ${c.step_number}`);
    else if (c.section_name) parts.push(c.section_name);
    if (c.page) parts.push(`p.${c.page}`);
    return (
        <a
            href={c.signed_url || "#"}
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-start gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2 text-xs hover:border-neutral-400 transition-colors"
            data-testid={`citation-${c.chunk_id}`}
        >
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-neutral-900 truncate">{parts.join(" · ") || "Source"}</div>
                <div className="text-neutral-500 mt-0.5 line-clamp-2">{c.excerpt}</div>
                <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-wider text-neutral-400">
                    <span>similarity {c.similarity.toFixed(2)}</span>
                    {c.signed_url && <ExternalLink size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
                </div>
            </div>
        </a>
    );
}

function Message({ m }) {
    if (m.role === "user") {
        return (
            <div className="flex justify-end mb-4" data-testid={`msg-user-${m.id}`}>
                <div className="max-w-2xl rounded-2xl bg-neutral-900 px-4 py-2.5 text-sm text-neutral-50">
                    {m.content}
                </div>
            </div>
        );
    }
    return (
        <div className="mb-6" data-testid={`msg-asst-${m.id}`}>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-400 mb-1.5">
                <Sparkles size={11} />
                <span>Ask AI{m.model && m.model !== "n/a" ? ` · ${m.model}` : ""}</span>
                {m.latency_ms ? <span>· {m.latency_ms}ms</span> : null}
                {!m.grounded && <span className="text-amber-600">· ungrounded</span>}
            </div>
            <div className="text-[15px] leading-relaxed text-neutral-800 whitespace-pre-wrap">
                {m.content}
            </div>
            {m.citations?.length > 0 && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {m.citations.map((c, i) => (
                        <div key={i} className="flex gap-2">
                            <span className="mt-2 text-[10px] font-mono text-neutral-400 shrink-0">S{i + 1}</span>
                            <CitationChip c={c} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function CorpusStats({ stats, onReindex, reindexing }) {
    if (!stats) return null;
    return (
        <Card className="mb-6 border-neutral-200" data-testid="corpus-stats-card">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                        <Info size={14} className="text-neutral-500" />
                        Knowledge base
                    </div>
                    <Button
                        size="sm" variant="outline"
                        disabled={reindexing}
                        onClick={onReindex}
                        data-testid="btn-reindex-corpus"
                    >
                        {reindexing ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <RefreshCw size={12} className="mr-1.5" />}
                        Reindex
                    </Button>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                        <div className="text-xl font-semibold text-neutral-900">{stats.approved_documents}</div>
                        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mt-0.5">Approved docs</div>
                    </div>
                    <div>
                        <div className="text-xl font-semibold text-neutral-900">{stats.documents_indexed}</div>
                        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mt-0.5">Indexed</div>
                    </div>
                    <div>
                        <div className="text-xl font-semibold text-neutral-900">{stats.total_chunks}</div>
                        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mt-0.5">Chunks</div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function AskAI() {
    const { user } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [q, setQ] = useState("");
    const [sending, setSending] = useState(false);
    const [stats, setStats] = useState(null);
    const [reindexing, setReindexing] = useState(false);
    const scrollRef = useRef(null);

    const isAdmin = user?.role === "ORG_ADMIN" || user?.role === "LD_ADMIN" || user?.role === "AUDITOR";

    async function loadConversations() {
        try {
            const r = await api.get("/ask-ai/conversations");
            setConversations(r.data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    async function loadConversation(id) {
        try {
            const r = await api.get(`/ask-ai/conversations/${id}`);
            setActiveId(id);
            setMessages(r.data.messages || []);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    async function loadStats() {
        if (!isAdmin) return;
        try {
            const r = await api.get("/ask-ai/corpus-stats");
            setStats(r.data);
        } catch (e) {
            // silent — non-admins won't have permission
        }
    }
    useEffect(() => { loadConversations(); loadStats(); /* eslint-disable-next-line */ }, []);
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, sending]);

    async function send() {
        const query = q.trim();
        if (!query || sending) return;
        setSending(true);
        setQ("");
        // Optimistic user message
        const tempUser = { id: `tmp-${Date.now()}`, role: "user", content: query };
        setMessages((prev) => [...prev, tempUser]);
        try {
            const r = await api.post("/ask-ai/query", {
                query, conversation_id: activeId,
            });
            const { conversation_id, user_message, assistant_message } = r.data;
            // Replace temp with real
            setMessages((prev) => {
                const filtered = prev.filter((m) => m.id !== tempUser.id);
                return [...filtered, user_message, assistant_message];
            });
            if (!activeId) {
                setActiveId(conversation_id);
                loadConversations();
            }
        } catch (e) {
            toast.error(formatApiError(e));
            setMessages((prev) => prev.filter((m) => m.id !== tempUser.id));
        } finally {
            setSending(false);
        }
    }

    async function newChat() {
        setActiveId(null);
        setMessages([]);
        setQ("");
    }
    async function reindex() {
        setReindexing(true);
        try {
            const r = await api.post("/ask-ai/reindex");
            toast.success(`Indexed ${r.data.documents_indexed} docs · ${r.data.chunks_written} chunks`);
            await loadStats();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setReindexing(false);
        }
    }
    async function archive(id) {
        try {
            await api.delete(`/ask-ai/conversations/${id}`);
            if (id === activeId) newChat();
            loadConversations();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const empty = messages.length === 0;
    const hasCorpus = stats ? stats.total_chunks > 0 : true;

    const examples = useMemo(() => [
        "What are the safety precautions for this procedure?",
        "What is the acceptance criterion for the electrode?",
        "Walk me through step 3 in the procedure.",
        "Which parameters must operators verify before starting?",
    ], []);

    return (
        <div className="flex gap-6 h-[calc(100vh-8rem)]" data-testid="page-ask-ai">
            {/* Sidebar — conversations */}
            <div className="w-64 shrink-0 flex flex-col">
                <Button
                    onClick={newChat}
                    className="mb-3 w-full justify-start"
                    variant="outline"
                    data-testid="btn-new-chat"
                >
                    <MessageSquare size={14} className="mr-2" />
                    New chat
                </Button>
                <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-400 mb-2 px-1">Recent</div>
                <div className="flex-1 overflow-y-auto space-y-0.5 pr-1">
                    {conversations.length === 0 && (
                        <div className="text-xs text-neutral-400 px-2 py-3" data-testid="conv-empty">
                            No conversations yet.
                        </div>
                    )}
                    {conversations.map((c) => (
                        <div key={c.id} className="group flex items-center gap-1">
                            <button
                                onClick={() => loadConversation(c.id)}
                                data-testid={`conv-${c.id}`}
                                className={`flex-1 text-left rounded-md px-2 py-1.5 text-sm truncate transition-colors ${
                                    activeId === c.id
                                        ? "bg-neutral-100 text-neutral-900"
                                        : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
                                }`}
                            >
                                {c.title}
                            </button>
                            <button
                                onClick={() => archive(c.id)}
                                data-testid={`btn-archive-${c.id}`}
                                title="Archive"
                                className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-neutral-700 px-1"
                            >
                                <Archive size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main chat */}
            <div className="flex-1 flex flex-col min-w-0">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Ask AI</h1>
                        <p className="text-sm text-neutral-500 mt-0.5">
                            Grounded on your organisation's approved documents. Citations included.
                        </p>
                    </div>
                </div>

                {isAdmin && <CorpusStats stats={stats} onReindex={reindex} reindexing={reindexing} />}

                {!hasCorpus && (
                    <Card className="mb-4 border-amber-200 bg-amber-50" data-testid="empty-corpus-warning">
                        <CardContent className="p-4 text-sm text-amber-900">
                            No approved documents in your knowledge base yet. Approve a document review to make it answerable.
                        </CardContent>
                    </Card>
                )}

                <div ref={scrollRef} className="flex-1 overflow-y-auto pr-2">
                    {empty ? (
                        <div className="flex flex-col items-center justify-center h-full text-center max-w-xl mx-auto">
                            <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
                                <Sparkles size={20} className="text-neutral-500" />
                            </div>
                            <h2 className="text-lg font-semibold text-neutral-900 mb-1">
                                Ask anything about your approved documents.
                            </h2>
                            <p className="text-sm text-neutral-500 mb-6">
                                Every answer is grounded in reviewed, approved SOPs with the exact source citation.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full">
                                {examples.map((ex) => (
                                    <button
                                        key={ex}
                                        onClick={() => setQ(ex)}
                                        data-testid={`example-${ex.slice(0, 24)}`}
                                        className="text-left rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50 transition-colors"
                                    >
                                        {ex}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div>
                            {messages.map((m) => <Message key={m.id} m={m} />)}
                            {sending && (
                                <div className="mb-6 text-neutral-400 text-sm flex items-center gap-2" data-testid="asst-thinking">
                                    <Loader2 size={14} className="animate-spin" />
                                    Retrieving from approved documents…
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="mt-4 flex items-end gap-2 border-t border-neutral-200 pt-4">
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder="Ask a question grounded in your approved documents…"
                        disabled={sending}
                        data-testid="ask-ai-input"
                        className="flex-1"
                    />
                    <Button
                        onClick={send}
                        disabled={!q.trim() || sending}
                        data-testid="btn-send"
                    >
                        {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </Button>
                </div>
                <div className="mt-2 text-[11px] text-neutral-400">
                    Ask AI can only answer using content from documents that have been reviewed and approved by your team.
                </div>
            </div>
        </div>
    );
}
